import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactPlaceholder from 'react-placeholder';
import InfiniteScroll from 'react-infinite-scroller';


import {
	useTheme,
	makeStyles,
	useMediaQuery,
	Typography,
	List,
	RootRef,
	Collapse
} from '@material-ui/core';

import { isJsonEqual } from "../utils/Json";
import Constants from "../utils/Constants";
import DeconstructActivity from "../utils/DeconstructActivity";
import ScrollableArea from "./ScrollableArea";
import ActivityListItem, { ItemPlaceholder } from "./ActivityListItem";
import CircularLoadingIndicator from "./CircularLoadingIndicator";

const rootDivPadding = 2;
const styles = makeStyles(theme => ({
	root: {
		//backgroundColor: "#e9e9e9",
		paddingLeft: theme.spacing(rootDivPadding),
		paddingRight: theme.spacing(rootDivPadding)
	},

	listEnd: {
		borderRadius: theme.spacing(0.5),
		borderTop: `${theme.spacing(2)}px solid #F4F5F5`
	},

	listEndTop: {
		borderBottomLeftRadius: 0,
		borderBottomRightRadius: 0,
	},

	listEndBottom: {
		borderTopLeftRadius: 0,
		borderTopRightRadius: 0,
	},

	listRoot: {
		paddingTop: 0,
		paddingBottom: 0
	},

	noActivity: {
		textAlign: "center",
		paddingTop: theme.spacing(3)
	},

	item: {
		marginBottom: theme.spacing(2),

		/*"&:last-child": {
			marginBottom: 0
		}*/
	}
}));

const placeholder = (
	<React.Fragment>
		{ItemPlaceholder}
		{ItemPlaceholder}
		{ItemPlaceholder}
	</React.Fragment>
);

const ActivityList = React.memo(function ActivityList(props) {
	const theme = useTheme();
	const scrollAreaRef = useRef();
	const [refesh, setRefresh] = useState();
	const isXs = useMediaQuery(theme.breakpoints.down("xs"));


	let classes = styles(),
		{ items, listHeightReduction,
			isLoading, loadMore, hasMore,
			clickHandler, selectedIndex, actionButtons,
			pulsateAlerts, scrollTo, animateOutId, onAnimateExited, fitToParent,
			currentStatus, scrollGridClassName, getScrollAreaRef, history, searchTerm } = props,
		heightReduction = listHeightReduction + theme.spacing(rootDivPadding + 2);

	// on mobile, when going to activity then back to list, loses scroll reference
	// so paging doesn't work. this just forces a re-render so it picks it up again
	useEffect(() => {
		if (isXs) {
			setRefresh(Math.random());
		}
	}, [setRefresh, isXs]);

	useEffect(() => {
		if (getScrollAreaRef) {
			getScrollAreaRef(scrollAreaRef);
		}
	}, [getScrollAreaRef]);

	return (
		<React.Fragment>
			<div className={`${classes.listEnd} ${classes.listEndTop}`}></div>
			<RootRef rootRef={scrollAreaRef}>
				<ScrollableArea
					scrollTo={scrollTo}
					fitToParent={fitToParent}
					heightReduction={heightReduction}
					scrollGridClassName={scrollGridClassName}>
					<div className={classes.root}>
						<ReactPlaceholder
							ready={items ? true : false}
							customPlaceholder={placeholder}>
							{items && items.length ?
								<List
									role="group"
									aria-label="signals list"
									classes={{ root: classes.listRoot }}>
									<React.Fragment>
										<InfiniteScroll
											useWindow={false}
											initialLoad={false}
											pageStart={1}
											loadMore={loadMore}
											hasMore={!isLoading && hasMore}
											getScrollParent={() => scrollAreaRef.current}
											loader={null}>
											{items.map((activity, idx) => {
												return (
													<DeconstructActivity
														key={activity.id}
														activity={activity}
														render={deconstructedProps => {
															let isSelected = selectedIndex === idx,
																listItem = (
																	<ActivityListItem
																		{...deconstructedProps}
																		history={history}
																		selected={isSelected}
																		className={classes.item}
																		pulsateAlerts={pulsateAlerts}
																		actionButtons={actionButtons}
																		currentStatus={currentStatus}
																		clickHandler={(activityId) => clickHandler(idx, activityId)}
																		searchTerm={searchTerm} />
																);

															return (
																<Collapse
																	timeout={250}
																	style={{ transitionDelay: '100ms' }}
																	in={animateOutId !== deconstructedProps.activityId}
																	onExited={() => onAnimateExited(deconstructedProps.activityId)}>{listItem}</Collapse>
															);
														}} />
												);
											})}
										</InfiniteScroll>
										{isLoading ? <CircularLoadingIndicator key={`indicator-${items.length}`} /> : null}
									</React.Fragment>
								</List>
								:
								<Typography className={classes.noActivity}>No Activity</Typography>
							}
						</ReactPlaceholder>
					</div>
				</ScrollableArea>
			</RootRef>
			<div className={`${classes.listEnd} ${classes.listEndBottom}`}></div>
		</React.Fragment>
	);
}, (prevProps, newProps) => {
	// if they're the same, don't re-render (return true)
	return isJsonEqual(prevProps, newProps);
});

ActivityList.displayName = "ActivityList";

ActivityList.defaultProps = {
	fidoError: false,
	listHeightReduction: 0
};

ActivityList.propTypes = {
	history: PropTypes.object,
	fidoError: PropTypes.bool,
	items: PropTypes.array,
	listHeightReduction: PropTypes.number,
	isLoading: PropTypes.bool,
	loadMore: PropTypes.func,
	hasMore: PropTypes.bool,
	//lastPage: PropTypes.number,
	clickHandler: PropTypes.func,
	selectedIndex: PropTypes.number,
	animateOutId: PropTypes.number,
	onAnimateExited: PropTypes.func,
	scrollTo: PropTypes.number,
	actionButtons: PropTypes.array,
	pulsateAlerts: PropTypes.bool,
	fitToParent: PropTypes.bool,
	scrollGridClassName: PropTypes.string,
	currentStatus: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	getScrollAreaRef: PropTypes.func
};

ActivityList.whyDidYouRender = Constants.ENV.IS_LOCAL;

export default ActivityList;